import { ChangeDetectionStrategy, Component, computed, inject, input, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AssetDto, GalleryDto, Tag, TenantDto } from '@nusign/picflow-backend-types';
import { filter, startWith, switchMap, take, tap } from 'rxjs';
import { EditTagDialogComponent } from '../../../gallery/dialogs/edit-tag-dialog/edit-tag-dialog.component';
import { AssetTagService } from '../../../services/asset-tag.service';
import { AssetService } from '../../../services/asset.service';
import { DialogService } from '../../../services/dialog.service';
import { PermissionService } from '../../../services/permission.service';
import { StateService } from '../../../services/state.service';
import { TagService } from '../../../services/tag.service';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { AutofocusDirective } from '../../../shared/directives/autofocus.directive';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';

@Component({
  selector: 'app-asset-tags',
  templateUrl: './asset-tags.component.html',
  styleUrls: ['./asset-tags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    SvgComponent,
    ButtonDirective,
    ContextMenuComponent,
    AutofocusDirective,
    TooltipDirective,
  ],
})
export class AssetTagsComponent {
  private tagService = inject(TagService);
  private assetService = inject(AssetService);
  private assetTagService = inject(AssetTagService);
  private state = inject(StateService);
  private dialog = inject(DialogService);
  private permissionService = inject(PermissionService);

  readonly asset = input<AssetDto>(undefined);
  readonly gallery = input<GalleryDto>(undefined);
  readonly tagTypeahead = viewChild<ContextMenuComponent>('tagTypeahead');

  // We need this until all components are correctly subscribing to asset updates
  liveAsset = toSignal(
    toObservable(this.asset).pipe(
      filter(Boolean),
      switchMap(asset => this.assetService.getAssetObservable(asset.id, asset.gallery as string))
    )
  );

  addTagForm = new FormGroup({
    name: new FormControl('', [Validators.required]),
    category: new FormControl(''),
  });

  private allTags = toSignal(this.tagService.tags$);

  tags = computed(() => {
    const allTags = this.allTags();
    const asset = this.liveAsset();
    if (!allTags || !asset) return [];
    return allTags.filter(tag => {
      if (asset.tags.some(at => at.id === tag.id)) return true;
      return false;
    });
  });

  allAvailableTags = computed(() => {
    const allTags = this.allTags();
    const tags = this.tags();
    if (!allTags || !tags) return [];
    return allTags.filter(tag => {
      if (tags.some(at => at.id === tag.id)) return false;
      return true;
    });
  });

  tagValue = toSignal(this.addTagForm.controls.name.valueChanges.pipe(startWith(this.addTagForm.controls.name.value)));

  filteredTags = computed(() => {
    const tags = this.allAvailableTags();
    const tagValue = this.tagValue();
    if (!tags) return [];
    if (!tagValue) return tags;
    const input = tagValue.toLowerCase();
    return tags.filter(tag => tag.name.toLowerCase().includes(input));
  });

  canCreate = computed(() => {
    const allTags = this.allTags();
    const tagValue = this.tagValue();
    const gallery = this.gallery();
    if (!allTags || !gallery || !tagValue) return false;
    const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    if (!this.state.tenants?.some(t => t.id === tenantId)) return false;
    if (!this.permissionService.hasRolePermissionLegacy('tags:create')) return false;
    const input = tagValue.toLowerCase();
    const alreadyExists = allTags.some(tag => tag.name.toLowerCase() === input);
    if (alreadyExists) return false;
    return true;
  });

  addTag() {
    if (!this.canCreate()) return;
    return this.tagService.tags$
      .pipe(
        take(1),
        tap(tags => {
          const existingTag = tags.find(t => t.name === this.addTagForm.value.name);
          if (existingTag) return this.addTagToAsset(existingTag);
          if (!this.addTagForm.valid) return;
          this.tagService.createTag(this.addTagForm.value.name, this.addTagForm.value.category).subscribe(tag => {
            this.addTagToAsset(tag);
          });
        })
      )
      .subscribe();
  }

  addTagToAsset(tag: Tag) {
    const asset = this.liveAsset();
    if (!asset.tags.some(t => t.id === tag.id)) {
      this.assetTagService.add(asset, tag).subscribe();
    }
    this.tagTypeahead()?.close();
    this.addTagForm.controls.name.setValue('');
  }

  removeTagFromAsset(tag: Tag, event: MouseEvent) {
    event.stopPropagation();
    this.assetTagService.remove(this.liveAsset(), tag).subscribe();
    this.tagTypeahead()?.close();
    this.addTagForm.controls.name.setValue('');
  }

  canEditTag() {
    const gallery = this.gallery();
    const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    if (!this.state.tenants?.some(t => t.id === tenantId)) return false;
    if (!this.permissionService.hasRolePermissionLegacy('tags:update')) return false;
    return true;
  }

  editTag(tag: Tag) {
    if (!this.canEditTag) return;
    this.dialog.open(EditTagDialogComponent, { tag });
  }

  isFocused(el: HTMLElement, event: MouseEvent) {
    event.stopPropagation();
    return el === document.activeElement;
  }
}
